import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import _, { map, isEmpty } from 'lodash';
import withPagination from './WithPagination';
import LoadingDataEnhancer from './LoadingData';
import externalVehiclesBatchesQuery from '../graphql/externalVehiclesBatchesQuery';
import ExternalVehicleBatchesList from '../views/ExternalVehicleBatches/List';
import Search from '../views/Shared/Search';
import DropdownSearch from '../views/ExternalVehicleBatches/DropdownSearch';
import { setSearch } from '../data/searchField/actions';
import { setPage } from '../data/page/actions';
import { setSecondarySearch } from '../data/secondarySearch/actions';
import { setSearchUser } from '../data/searchUser/actions';

const ExternalVehicleBatches = props => {
  const {
    data: { externalVehiclesBatches, externalVehiclesBatchUsers, refetch },
    searchFields,
    secondarySearch,
    refreshSearch,
    dispatch,
    setSearchFields: setSearchFieldsAction,
    setPage: setPageAction,
    setSecondarySearchFields,
  } = props;

  const refetchData = useCallback((sFields, secSearch) => {
    const status = secSearch.status === 'ALL' ? null : secSearch.status;
    const variableArguments = {
      vin: sFields.vin || null,
      batchId: sFields.batchId || null,
      dealerNumber: sFields.dealerNumber || null,
      userId: parseInt(secSearch.userId, 10) || null,
      status,
    };
    refetch({
      ...variableArguments,
    });
  }, [refetch]);

  useEffect(() => {
    if (refreshSearch) {
      refetchData(searchFields, secondarySearch);
    }

    if (isEmpty(searchFields) && isEmpty(secondarySearch)) {
      const all = { text: 'All', key: 0, value: 'ALL' };
      const userList = externalVehiclesBatchUsers.map(data => ({
        text: data.username,
        key: parseInt(data.id, 10),
        value: parseInt(data.id, 10),
      }));

      const usersArray = [all];
      const users = _.uniqWith(userList, _.isEqual);
      map(users, u => {
        usersArray.push(u);
      });
      dispatch(setSearchUser(usersArray));
    }
  }, [
    refreshSearch,
    searchFields,
    secondarySearch,
    externalVehiclesBatchUsers,
    dispatch,
    refetchData,
  ]);

  const onSearchSubmitHandler = useCallback(event => {
    const { dealerNumber, vin, batchId } = event.target;
    const dealerNo = isEmpty(dealerNumber.value) ? null : dealerNumber.value;
    const batch = parseInt(batchId.value, 10);
    const status = secondarySearch.status === 'ALL' ? null : secondarySearch.status;
    const variableArguments = {
      vin: vin.value || null,
      batchId: batch || null,
      dealerNumber: dealerNo || null,
      userId: parseInt(secondarySearch.userId, 10) || null,
      status,
    };
    refetch({
      ...variableArguments,
    });
    setSearchFieldsAction(variableArguments);
    setPageAction({});
  }, [secondarySearch, refetch, setSearchFieldsAction, setPageAction]);

  const onSelectApprovalStatus = useCallback((approvalStatus, userId) => {
    const status = approvalStatus === 'ALL' ? null : approvalStatus;
    const variableArguments = {
      status,
      userId: parseInt(userId, 10) || null,
      vin: searchFields.vin || null,
      batchId: searchFields.batchId || null,
      dealerNumber: searchFields.dealerNumber || null,
    };
    refetch({
      ...variableArguments,
    });
    setSecondarySearchFields({ status: approvalStatus, userId });
    setPageAction({});
  }, [searchFields, refetch, setSecondarySearchFields, setPageAction]);

  return (
    <div>
      <h2> External Vehicle Batches </h2>
      <Search
        onSubmit={onSearchSubmitHandler}
        searchFields={searchFields}
        searchOptions={['dealerNumber', 'vin', 'batchId']}
        multipleSearches
      />
      <DropdownSearch
        onSelectApprovalStatus={onSelectApprovalStatus}
        externalVehiclesBatches={externalVehiclesBatches}
        searchFields={secondarySearch}
        searchOptions={['status', 'userId']}
      />
      <ExternalVehicleBatchesList
        externalVehicleBatches={externalVehiclesBatches}
      />
    </div>
  );
};

ExternalVehicleBatches.propTypes = {
  data: PropTypes.shape({
    externalVehiclesBatches: PropTypes.array,
    externalVehiclesBatchUsers: PropTypes.array,
    refetch: PropTypes.func,
  }).isRequired,
  searchFields: PropTypes.shape({
    vin: PropTypes.string,
    batchId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    dealerNumber: PropTypes.string,
  }).isRequired,
  secondarySearch: PropTypes.shape({
    status: PropTypes.string,
    userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  refreshSearch: PropTypes.bool,
  dispatch: PropTypes.func.isRequired,
  setSearchFields: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  setSecondarySearchFields: PropTypes.func.isRequired,
};

ExternalVehicleBatches.defaultProps = {
  refreshSearch: false,
};

const mapDispatchToProps = dispatch => ({
  setSearchFields: fields => {
    dispatch(setSearch(fields));
  },
  setSecondarySearchFields: fields => {
    dispatch(setSecondarySearch(fields));
  },
  setPage: () => {
    dispatch(setPage());
  },
  dispatch,
});

const mapStateToProps = state => {
  const {
    data: {
      searchFields, secondarySearch, refreshSearch, page,
    },
  } = state;
  return {
    searchFields,
    secondarySearch,
    refreshSearch,
    page,
  };
};

export default compose(
  withRouter,
  withPagination(externalVehiclesBatchesQuery, 'externalVehiclesBatches'),
  LoadingDataEnhancer,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(ExternalVehicleBatches);
